import React, { useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { Icon } from "@iconify/react";
import SignupModal from "./signupmodal";
import ForgotPasswordModal from "./forgot_password_modal";
import ChangePasswordModal from "./change_password_modal";
import Common from "../../helpers/common";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  AuthenticationViewTypeEnum,
  RoleEnum,
  VerificationTypeEnum,
} from "../../constants/appConstants";
import { getIsBookingFlow, setIsBookingFlow } from "../../store/bookingSlice";
import Swal from "sweetalert2";
import OtpModal from "./otp_modal";
import { PhoneInputField } from "../formik-components";
import CustomPhoneInput from "./CustomPhoneInput";

// Dynamic validation schema that changes based on input type
const getValidationSchema = (isPhoneMode) => {
  if (isPhoneMode) {
    return Yup.object().shape({
      phone: Yup.string().required("Phone number is required"),
      password: Yup.string().required("Password is required"),
    });
  } else {
    return Yup.object().shape({
      emailorphone: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      password: Yup.string().required("Password is required"),
    });
  }
};

const LoginModal = ({ open, onClose, onLoginSuccess }) => {
  const [viewType, setViewType] = useState(AuthenticationViewTypeEnum.Login);
  const [isLoading, setIsLoading] = useState(false);
  const [forgotPasswordData, setForgotPasswordData] = useState(null);
  const [verificationId, setVerificationId] = useState(null);
  const [isPhoneMode, setIsPhoneMode] = useState(false);
  const [shouldFocusPhone, setShouldFocusPhone] = useState(false);
  const emailInputRef = React.useRef(null);
  var navigate = useNavigate();
  const dispatch = useDispatch();
  const isBookingFlow = useSelector(getIsBookingFlow);

  if (!open) return null;

  // Function to detect if input is a phone number
  const detectInputType = (value) => {
    if (!value || value.trim() === "") {
      return false; // Default to email mode when empty
    }

    const trimmedValue = value.trim();

    // If it contains @ symbol, it's definitely an email
    if (trimmedValue.includes('@')) {
      return false;
    }

    // Check if it starts with + or has digits
    const digitCount = (trimmedValue.match(/\d/g) || []).length;

    // Phone number detection rules (improved):
    // 1. Starts with + (international format)
    // 2. Starts with a digit and has at least 3 digits total
    // 3. Contains phone-like patterns with dashes/spaces
    const startsWithPlus = trimmedValue.startsWith('+');
    const startsWithDigit = /^\d/.test(trimmedValue);
    const hasPhoneLikeFormat = /^[\d\s\-\(\)]+$/.test(trimmedValue) && digitCount >= 3;

    return startsWithPlus || (startsWithDigit && digitCount >= 3) || hasPhoneLikeFormat;
  };

  // Handle input change and mode switching
  const handleInputChange = (value, formikProps) => {
    const shouldBePhoneMode = detectInputType(value);

    if (shouldBePhoneMode !== isPhoneMode) {
      setIsPhoneMode(shouldBePhoneMode);

      if (shouldBePhoneMode) {
        // Switching to phone mode
        const phoneValue = value.startsWith('+') ? value.replace('+', '') : '92' + value;
        formikProps.setFieldValue("phone", phoneValue);
        formikProps.setFieldValue("phoneCode", "+92");
        formikProps.setFieldValue("phoneNum", value.replace(/^\+?92/, ""));
        formikProps.setFieldValue("emailorphone", "");
        setShouldFocusPhone(true);
      } else {
        // Switching to email mode
        formikProps.setFieldValue("emailorphone", value);
        formikProps.setFieldValue("phone", "");
        formikProps.setFieldValue("phoneCode", "");
        formikProps.setFieldValue("phoneNum", "");
        setShouldFocusPhone(false);
      }
    } else {
      // Same mode, just update the value
      if (isPhoneMode) {
        formikProps.setFieldValue("phone", value);
      } else {
        formikProps.setFieldValue("emailorphone", value);
      }
      setShouldFocusPhone(false);
    }
  };

  const handleSignupClick = () => {
    setViewType(AuthenticationViewTypeEnum.Signup);
  };

  const handleForgotPasswordClick = () => {
    setViewType(AuthenticationViewTypeEnum.ForgotPassword);
  };

  const handleForgotPasswordOtpNavigation = (data) => {
    setForgotPasswordData(data);
    setViewType(AuthenticationViewTypeEnum.Otp);
  };

  const handleChangePasswordNavigation = (vCodeId) => {
    setVerificationId(vCodeId);
    setViewType(AuthenticationViewTypeEnum.ChangePassword);
  };

  const handlePasswordChangeSuccess = () => {
    setViewType(AuthenticationViewTypeEnum.Login);
    setForgotPasswordData(null);
    setVerificationId(null);
  };

  // Function to clean phone number - remove dashes, spaces, parentheses, etc.
  const cleanPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    // Remove all non-numeric characters except +
    return phoneNumber.replace(/[^\d+]/g, '');
  };

  const _handleLogin = async (values) => {
    console.log("🔐 Login form submitted:", values);
    console.log("📊 Booking flow status:", isBookingFlow);

    try {
      setIsLoading(true);
      Common.ApiService.getInstance().initialize(
        Common.AppConstant.serviceUrl,
        {
          "Content-Type": "application/json",
        }
      );

      // Prepare login data based on current mode
      let loginData;
      if (isPhoneMode) {
        // For phone mode, clean the phone number and send only numeric part
        const rawPhoneNumber = values.phoneNum || values.phone?.replace('92', '') || '';
        const cleanedPhoneNumber = cleanPhoneNumber(rawPhoneNumber);
        loginData = {
          emailorphone: cleanedPhoneNumber.trim(),
          password: values.password,
        };
      } else {
        loginData = {
          emailorphone: values.emailorphone,
          password: values.password,
        };
      }

      const response = await Common.ApiService.getInstance()?.request(
        "Signin",
        loginData,
        "POST"
      );
      console.log("✅ Login response:", response);

      // Check if response exists and has success status
      if (response && response.status === 1 && response.data) {
        let user = response.data[0];
        const sessionExpiry = user.jwtExpiryDate ? new Date(user.jwtExpiryDate) : 30;
        Common.setToken(user.token, sessionExpiry);
        Common.setUser(user);
        global.user = user;

        console.log("👤 User set successfully:", user.firstName);

        // Check if this is a booking flow
        if (isBookingFlow && onLoginSuccess) {
          // If booking flow, call onLoginSuccess callback
          console.log("🎯 Booking flow detected - calling onLoginSuccess");
          onClose();
          onLoginSuccess();
        } else {
          // Normal login flow - redirect to dashboard
          console.log("🏠 Normal login - redirecting to dashboard");
          let _route;
          if (global.user.roleId == RoleEnum.Player) {
            _route = "/dashboard";
          }
          onClose();
          setTimeout(() => {
            navigate(_route, { replace: true });
            // Removed window.location.reload() for faster experience
          }, 100);
        }
      } else {
        // Login failed - show error message
        console.log("❌ Login failed:", response);
        const errorMessage = response?.message || "Invalid email or password";

        // Show error using SweetAlert for consistency
        await Swal.fire({
          icon: "error",
          title: "Login Failed",
          text: errorMessage,
          confirmButtonText: "OK",
          background: "#1a1a1a",
          color: "#fff",
          confirmButtonColor: "#007bff",
        });
      }
    } catch (e) {
      console.log("❌ Login error:", e);
      // Show error for network or other issues
      await Swal.fire({
        icon: "error",
        title: "Connection Error",
        text: "Unable to connect to server. Please try again.",
        confirmButtonText: "OK",
        background: "#1a1a1a",
        color: "#fff",
        confirmButtonColor: "#007bff",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // return <OtpModal open={true} onClose={() => {}} onVerify={() => {}} />;

  if (viewType == AuthenticationViewTypeEnum.Signup) {
    return (
      <SignupModal
        open={true}
        onClose={() => {
          setViewType(AuthenticationViewTypeEnum.Login);
          onClose();
        }}
        setViewType={setViewType}
        onLoginClick={() => setViewType(AuthenticationViewTypeEnum.Login)}
        onLoginSuccess={onLoginSuccess}
      />
    );
  }

  if (viewType == AuthenticationViewTypeEnum.ForgotPassword) {
    return (
      <ForgotPasswordModal
        onClose={() => {
          setViewType(AuthenticationViewTypeEnum.Login);
          onClose();
        }}
        onOtpNavigation={handleForgotPasswordOtpNavigation}
        onBackToLogin={() => setViewType(AuthenticationViewTypeEnum.Login)}
      />
    );
  }

  if (viewType == AuthenticationViewTypeEnum.Otp) {
    return (
      <OtpModal
        open={true}
        onClose={() => {
          setViewType(AuthenticationViewTypeEnum.Login);
          onClose();
        }}
        onLoginSuccess={onLoginSuccess}
        forgotPasswordData={forgotPasswordData}
        verificationType={
          forgotPasswordData
            ? VerificationTypeEnum.forgotPassword
            : VerificationTypeEnum.accountVerificationPhone
        }
        onChangePasswordNavigation={handleChangePasswordNavigation}
      />
    );
  }

  if (viewType == AuthenticationViewTypeEnum.ChangePassword) {
    return (
      <ChangePasswordModal
        onClose={() => {
          setViewType(AuthenticationViewTypeEnum.Login);
          onClose();
        }}
        verificationId={verificationId}
        onSuccess={handlePasswordChangeSuccess}
      />
    );
  }

  return (
    <div className="popup">
      <div
        className="popup-inner"
        style={{
          backgroundColor: "#1a1a1a",
          color: "white",
          top: "50%",

          border: "1px solid",
          maxWidth: "400px",
          width: "90%",
          boxShadow: "0 0 20px rgba(255, 255, 255, 0.1)", // Light glow effect
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            right: "15px",
            top: "15px",
            background: "none",
            border: "none",
            color: "white",
            fontSize: "28px",
            cursor: "pointer",
            padding: "5px 10px",
            lineHeight: "1",
            zIndex: "1000",
          }}
        >
          ×
        </button>

        <div
          style={{
            textAlign: "center",
            marginBottom: "20px",
            marginTop: "10px",
          }}
        >
          <img
            src="/images/logo_white.svg"
            alt="Ignite Sports"
            style={{
              width: "120px",
              height: "auto",
            }}
          />
        </div>

        <h2
          style={{
            textAlign: "center",
            color: "white",
            marginBottom: "6px",
            fontSize: "28px",
            fontWeight: "bold",
          }}
        >
          Login
        </h2>

        <Formik
          initialValues={{
            emailorphone: "",
            password: "",
            phone: "",
            phoneCode: "+92",
            phoneNum: "",
          }}
          validationSchema={getValidationSchema(isPhoneMode)}
          onSubmit={_handleLogin}
        >
          {(formikProps) => (
            <form onSubmit={formikProps.handleSubmit}>
              <div style={{ marginBottom: "0px" }} key={isPhoneMode ? 'phone' : 'email'}>
                {isPhoneMode ? (
                  <div>
                    <label
                      style={{
                        color: "#999",
                        marginBottom: "8px",
                        display: "block",
                        fontSize: "14px",
                      }}
                    >
                      PHONE
                    </label>
                    <CustomPhoneInput
                      formikProps={formikProps}
                      shouldFocus={shouldFocusPhone}
                      onChange={(value, countryData, numberOnly) => {
                        // Only switch to email if the number part is completely empty
                        if (!numberOnly || numberOnly.trim() === "") {
                          setIsPhoneMode(false);
                          formikProps.setFieldValue("phone", "");
                          formikProps.setFieldValue("phoneCode", "");
                          formikProps.setFieldValue("phoneNum", "");
                          formikProps.setFieldValue("emailorphone", "");
                          setShouldFocusPhone(false);

                          // Focus the email input when switching back
                          setTimeout(() => {
                            if (emailInputRef.current) {
                              emailInputRef.current.focus();
                            }
                          }, 100);
                        }
                      }}
                    />
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        marginTop: "5px",
                        cursor: "pointer",
                        textDecoration: "underline"
                      }}
                      onClick={() => {
                        setIsPhoneMode(false);
                        formikProps.setFieldValue("phone", "");
                        formikProps.setFieldValue("phoneCode", "");
                        formikProps.setFieldValue("phoneNum", "");
                        formikProps.setFieldValue("emailorphone", "");
                      }}
                    >
                      Switch to email
                    </div>
                  </div>
                ) : (
                  <div>
                    <label
                      style={{
                        color: "#999",
                        marginBottom: "8px",
                        display: "block",
                        fontSize: "14px",
                      }}
                    >
                      EMAIL / PHONE
                    </label>
                    <input
                      ref={emailInputRef}
                      type="text"
                      name="emailorphone"
                      value={formikProps.values.emailorphone}
                      onChange={(e) => {
                        handleInputChange(e.target.value, formikProps);
                      }}
                      onKeyUp={(e) => {
                        handleInputChange(e.target.value, formikProps);
                      }}
                      onBlur={formikProps.handleBlur}
                      placeholder="Enter email or phone number"
                      style={{
                        width: "100%",
                        padding: "4px",
                        backgroundColor: "#2d2d2d",
                        border: "1px solid #3d3d3d",
                        borderRadius: "4px",
                        color: "white",
                        fontSize: "16px",
                      }}
                    />
                    {formikProps.touched.emailorphone &&
                      formikProps.errors.emailorphone && (
                        <div
                          style={{
                            color: "#ff4d4d",
                            fontSize: "12px",
                            marginTop: "5px",
                          }}
                        >
                          {formikProps.errors.emailorphone}
                        </div>
                      )}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    color: "#999",
                    marginBottom: "8px",
                    display: "block",
                    fontSize: "14px",
                  }}
                >
                  PASSWORD
                </label>
                <input
                  type="password"
                  name="password"
                  value={formikProps.values.password}
                  onChange={formikProps.handleChange}
                  onBlur={formikProps.handleBlur}
                  placeholder="Password"
                  style={{
                    width: "100%",
                    padding: "4px",
                    backgroundColor: "#2d2d2d",
                    border: "1px solid #3d3d3d",
                    borderRadius: "4px",
                    color: "white",
                    fontSize: "16px",
                  }}
                />
                {formikProps.touched.password &&
                  formikProps.errors.password && (
                    <div
                      style={{
                        color: "#ff4d4d",
                        fontSize: "12px",
                        marginTop: "5px",
                      }}
                    >
                      {formikProps.errors.password}
                    </div>
                  )}
              </div>

              <div style={{ textAlign: "center", marginTop: "15px" }}>
                <button
                  type="button"
                  onClick={handleForgotPasswordClick}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#999",
                    cursor: "pointer",
                    fontSize: "14px",
                    textDecoration: "underline",
                  }}
                >
                  Forgot Password?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                style={{
                  width: "100%",
                  backgroundColor: "white",
                  color: "black",
                  marginTop: "25px",
                  padding: "4px",
                  borderRadius: "4px",
                  border: "none",
                  fontSize: "16px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  transition: "background-color 0.3s",
                }}
                onMouseOver={(e) =>
                  (e.target.style.backgroundColor = "#e6e6e6")
                }
                onMouseOut={(e) => (e.target.style.backgroundColor = "white")}
              >
                {isLoading ? "Logging in..." : "Log in"}
              </button>

              <div
                style={{
                  textAlign: "center",
                  marginTop: "25px",
                  color: "#999",
                }}
              >
                Don't have an account?{" "}
                <button
                  type="button"
                  onClick={handleSignupClick}
                  style={{
                    background: "none",
                    border: "none",
                    color: "white",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: "14px",
                  }}
                >
                  Sign Up
                </button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default LoginModal;
